import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ieltsDiagnosticApi,
  IELTS_SKILL_ROUTE,
  type DiagnosticSkill,
  type IeltsSkill,
} from '@/api/ielts';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { withoutClosed } from '@/lib/aiEntries';
import { useAppConfig } from '@/hooks/useAppConfig';

const SKILL_ICON: Record<IeltsSkill, string> = {
  listening: 'headphones',
  reading: 'menu_book',
  writing: 'edit_note',
  speaking: 'mic',
};

/** Band → 0..1 fill for the overall ring (IELTS max is 9). */
function bandFraction(band?: number | null): number {
  if (typeof band !== 'number') return 0;
  return Math.max(0, Math.min(1, band / 9));
}

/**
 * IELTS Diagnostic (`/app/ielts/diagnostic`) — the "mock exam" entry. Each of the
 * four skills is taken separately through its own trainer; this page shows the
 * per-skill status and, once all four are done, the overall IELTS band (the
 * student's level) and a hand-off to the trainers.
 */
export function StudentIELTSDiagnosticPage() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['ielts', 'diagnostic'], queryFn: ieltsDiagnosticApi.get });
  // Карточка навыка ведёт на его экран; экран речи закрыт (D22, 11.09.2026).
  useAppConfig();

  const done = q.data ? q.data.skills.filter((s) => s.band !== null).length : 0;

  return (
    <>
      <PageHeader title={t('ielts.diagnostic.title')} subtitle={t('ielts.diagnostic.subtitle')} />

      {q.isLoading && <Card variant="subtle">{t('common.loading')}</Card>}
      {q.isError && (
        <Card variant="subtle" className="text-error">
          {humanizeError(q.error)}
        </Card>
      )}

      {q.data && (
        <div className="space-y-5">
          {/* Overall level — a hero once the diagnostic is complete, else progress */}
          {q.data.complete ? (
            <Card className="bg-gradient-to-br from-primary to-primary-pressed text-on-primary">
              <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
                <OverallRing band={q.data.overall_band} />
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-xs font-semibold uppercase tracking-label text-on-primary/80">
                    {t('ielts.diagnostic.levelReady')}
                  </p>
                  <CardTitle className="mt-1 text-on-primary">
                    {t('ielts.diagnostic.yourLevel', {
                      band: (q.data.overall_band ?? 0).toFixed(1),
                    })}
                  </CardTitle>
                  <p className="mt-2 text-sm text-on-primary/90">
                    {t('ielts.diagnostic.levelHint')}
                  </p>
                  <Link to="/app/trainers" className="mt-4 inline-block">
                    <Button variant="secondary" size="sm">
                      <span className="material-symbols-outlined text-base">exercise</span>
                      {t('ielts.diagnostic.goTrainers')}
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ) : (
            <Card variant="subtle">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle>{t('ielts.diagnostic.inProgressTitle')}</CardTitle>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    {t('ielts.diagnostic.inProgressHint')}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-display text-3xl font-extrabold text-primary">{done}/4</p>
                  <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                    {t('ielts.diagnostic.sectionsDone')}
                  </p>
                </div>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-pill bg-surface-container">
                <div
                  className="h-full rounded-pill bg-primary transition-all"
                  style={{ width: `${(done / 4) * 100}%` }}
                />
              </div>
            </Card>
          )}

          {/* The four skill sections */}
          <ul className="grid gap-3 md:grid-cols-2">
            {withoutClosed(q.data.skills, (s) => IELTS_SKILL_ROUTE[s.skill]).map((s) => (
              <li key={s.skill}>
                <SkillCard skill={s} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function SkillCard({ skill }: { skill: DiagnosticSkill }) {
  const { t } = useTranslation();
  const done = skill.band !== null;
  /** Translate a weak-area key (question type or criterion), falling back to the raw key. */
  const areaLabel = (key: string) => t(`ielts.diagnostic.area.${key}`, { defaultValue: key });

  return (
    <Card className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'grid h-12 w-12 shrink-0 place-items-center rounded-card',
            done ? 'bg-success-container text-success' : 'bg-primary-container text-primary',
          )}
        >
          <span className="material-symbols-outlined">{SKILL_ICON[skill.skill]}</span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CardTitle className="truncate">{t(`ielts.diagnostic.skill.${skill.skill}`)}</CardTitle>
            {done ? (
              <Pill variant="success">{t('ielts.diagnostic.bandShort', { band: skill.band!.toFixed(1) })}</Pill>
            ) : (
              <Pill variant="neutral">{t('ielts.diagnostic.notStarted')}</Pill>
            )}
          </div>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            {done
              ? t('ielts.diagnostic.attempts', { count: skill.attempts })
              : t('ielts.diagnostic.takeToDiagnose')}
          </p>
        </div>
        <Link to={IELTS_SKILL_ROUTE[skill.skill]} className="shrink-0">
          <Button size="sm" variant={done ? 'ghost' : 'secondary'}>
            {done ? t('ielts.diagnostic.retake') : t('ielts.diagnostic.start')}
          </Button>
        </Link>
      </div>

      {/* Weak areas to drill (question types / criteria) — the targeted part */}
      {done && skill.weak.length > 0 && (
        <div className="rounded-card bg-warning-container/40 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-label text-on-surface-variant">
            {t('ielts.diagnostic.focusOn')}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {skill.weak.map((w) => (
              <span
                key={w}
                className="rounded-pill bg-surface-container-lowest px-2.5 py-1 text-xs font-medium text-on-surface ghost-border"
              >
                {areaLabel(w)}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function OverallRing({ band }: { band?: number | null }) {
  const deg = Math.round(bandFraction(band) * 360);
  return (
    <div
      className="grid h-32 w-32 shrink-0 place-items-center rounded-full"
      style={{ background: `conic-gradient(#ffffff ${deg}deg, rgba(255,255,255,0.25) ${deg}deg)` }}
    >
      <div className="grid h-24 w-24 place-items-center rounded-full bg-primary">
        <span className="font-display text-4xl font-extrabold text-on-primary">
          {typeof band === 'number' ? band.toFixed(1) : '—'}
        </span>
      </div>
    </div>
  );
}
